import { appConfig } from 'config/customization';
import { message } from 'utils/stringUtils';
import Images from 'config/assets';
import { ROUTES } from 'constants/routes';

import { checkSubOptions } from './utils';

export const NEW_CLAIM = 'new_claim';
export const NEW_PROCEDURE = 'new_procedure';
export const CREATE_FORM = 'create_form';
export const TECHNICAL_SERVICE_OPTION = message('Sidebar:technicalService').toUpperCase();
export const CUSTOMER_SUPPORT_OPTION = message('Sidebar:customerSupport').toUpperCase();
export const INVOICES_PAYMENTS_OPTION = message('Sidebar:invoicesAndPayments').toUpperCase();

export const mobileOptions = [
  {
    asset: Images.sidebarPoints,
    optionName: message('Sidebar:points').toUpperCase(),
    linkTo: ROUTES.POINTS,
    isActive: false,
    show: appConfig.usePoints
  },
  {
    asset: Images.sidebarSettings,
    optionName: message('Sidebar:settings').toUpperCase(),
    linkTo: ROUTES.CONFIGURATION,
    isActive: false,
    show: true
  },
  {
    asset: Images.sidebarAccountStatus,
    optionName: message('ClientSummary:clientSummary').toUpperCase(),
    linkTo: ROUTES.CLIENT_SUMMARY,
    isActive: false,
    show: appConfig.useClientSummary
  }
].filter(option => option.show);

function getSidebarOptions(currentAccount, isPublic, location, claimCategories) {
  let claimSuboptions = [];
  if (claimCategories.length > 0) {
    for (let index = 0; index < claimCategories.length; index += 1) {
      const mappedOptions = claimCategories[index].forms.map(form => ({
        optionName: form.name,
        show: true,
        key: CREATE_FORM,
        id: form.id
      }));
      claimSuboptions = [...claimSuboptions, ...mappedOptions];
    }
  }

  let claimOptions;

  if (appConfig.claims.showClaimFormsOnSidebar && claimSuboptions.length) {
    claimOptions = claimSuboptions;
  } else if (!appConfig.claims.openClaimsFromSidebar && !appConfig.claims.showClaimFormsOnSidebar) {
    claimOptions = [
      {
        optionName: message('Sidebar:newClaim'),
        key: NEW_CLAIM,
        show: appConfig.claims.useClaims && !appConfig.claims.showClaimFormsOnSidebar
      },
      {
        optionName: message('Sidebar:claims'),
        linkTo: ROUTES.CLAIMS,
        show: appConfig.claims.useClaimList,
        isActive: location === ROUTES.CLAIMS
      },
      appConfig.complaints.includeInTechnicalService && {
        optionName: message('Sidebar:complaints'),
        linkTo: ROUTES.COMPLAINTS,
        show: appConfig.complaints.useComplaints,
        isActive: location === ROUTES.COMPLAINTS
      }
    ].filter(submenu => submenu.show);
  }

  const privateSideBarOptions = [
    {
      id: 1,
      asset: Images.sidebarAccountStatus,
      optionName: `${message('Sidebar:accountStatus').toUpperCase()} ${
        currentAccount && appConfig.homeButtonWithClientNumber ? currentAccount.client_number : ''
      }`,
      linkTo: ROUTES.ACCOUNT_STATUS,
      detail: currentAccount && currentAccount.address,
      isActive: location === ROUTES.ACCOUNT_STATUS,
      show: true
    },
    {
      id: 2,
      asset: Images.sidebarBills,
      optionName: message('Sidebar:invoicesAndPayments').toUpperCase(),
      isActive:
        location === ROUTES.BILLS || location === ROUTES.PAYMENTS || location === ROUTES.CHANGE_DUE_DATE,
      show: true,
      options: [
        {
          optionName: message('Sidebar:invoicesList'),
          linkTo: ROUTES.BILLS,
          show: true,
          isActive: location === ROUTES.BILLS
        },
        {
          optionName: message('Sidebar:documentsAndPayments'),
          linkTo: ROUTES.PAYMENTS,
          show: appConfig.payments.active,
          isActive: location === ROUTES.PAYMENTS
        },
        {
          optionName: message('Sidebar:changeDueDate'),
          linkTo: ROUTES.CHANGE_DUE_DATE,
          show: appConfig.changeDueDate.active,
          isActive: location === ROUTES.CHANGE_DUE_DATE
        }
      ].filter(submenu => submenu.show)
    },
    {
      id: 3,
      asset: Images.sidebarTechnicalService,
      optionName: message('Sidebar:technicalService').toUpperCase(),
      isActive:
        location === ROUTES.CLAIMS ||
        (appConfig.complaints.includeInTechnicalService && location === ROUTES.COMPLAINTS),
      key: appConfig.claims.openClaimsFromSidebar && NEW_CLAIM,
      options: claimOptions,
      show: true
    },
    {
      id: 4,
      asset: Images.sidebarCustomerSupport,
      optionName: message('Sidebar:customerSupport').toUpperCase(),
      isActive:
        location === ROUTES.PROCEDURES ||
        (!appConfig.complaints.includeInTechnicalService && location === ROUTES.COMPLAINTS) ||
        location === ROUTES.SOCIAL_RATE ||
        location === ROUTES.NEW_TERMINATION_REQUEST ||
        location === ROUTES.NEW_ACCOUNT_HOLDER_CHANGE ||
        location === ROUTES.PENDING_FINE_AMOUNT_QUERY ||
        location === ROUTES.PENDING_WARRANTY_DEPOSIT_QUERY ||
        location === ROUTES.REGISTER_ACCOUNT,
      show: true,
      options: [
        {
          optionName: message('Sidebar:myTurns'),
          linkTo: ROUTES.MY_TURNS,
          show: appConfig.turns.active,
          isActive: location === ROUTES.MY_TURNS || location === ROUTES.NEW_TURN
        },
        {
          optionName: message('Sidebar:newProcedure'),
          key: NEW_PROCEDURE,
          show: appConfig.procedures.useProcedures
        },
        {
          optionName: message('Sidebar:procedures'),
          linkTo: ROUTES.PROCEDURES,
          show: appConfig.procedures.useProcedureList,
          isActive: location === ROUTES.PROCEDURES
        },
        !appConfig.complaints.includeInTechnicalService && {
          optionName: message('Sidebar:complaints'),
          linkTo: ROUTES.COMPLAINTS,
          show: appConfig.complaints.useComplaints,
          isActive: location === ROUTES.COMPLAINTS
        },
        {
          optionName: message('Sidebar:terminationRequest'),
          linkTo: ROUTES.NEW_TERMINATION_REQUEST,
          show: appConfig.includesTerminationService,
          isActive: location === ROUTES.NEW_TERMINATION_REQUEST
        },
        {
          optionName: message('Sidebar:accountHolderChange'),
          linkTo: ROUTES.NEW_ACCOUNT_HOLDER_CHANGE,
          show: appConfig.includesHolderChangeProcedure,
          isActive: location === ROUTES.NEW_ACCOUNT_HOLDER_CHANGE
        },
        {
          optionName: message('Sidebar:registerAccount'),
          linkTo: ROUTES.REGISTER_ACCOUNT,
          show: appConfig.includesRegisterAccount,
          isActive: location === ROUTES.REGISTER_ACCOUNT
        },
        {
          optionName: message('Sidebar:socialRate'),
          linkTo: ROUTES.SOCIAL_RATE,
          show: appConfig.socialRate.useSocialRate,
          isActive: location === ROUTES.SOCIAL_RATE
        },
        {
          optionName: message('Sidebar:consultDepositWarranty'),
          linkTo: ROUTES.PENDING_WARRANTY_DEPOSIT_QUERY,
          isActive: location === ROUTES.PENDING_WARRANTY_DEPOSIT_QUERY,
          show: appConfig.dashboard.pendingWarrantyDepositQuery
        },
        {
          optionName: message('Sidebar:consultFineQuality'),
          linkTo: ROUTES.PENDING_FINE_AMOUNT_QUERY,
          isActive: location === ROUTES.PENDING_FINE_AMOUNT_QUERY,
          show: appConfig.dashboard.pendingFineAmountQuery
        }
      ].filter(submenu => submenu.show)
    },

    {
      id: 5,
      asset: Images.sidebarAdvisor,
      optionName: message('Sidebar:energyAdvisor').toUpperCase(),
      linkTo: ROUTES.ENERGY_ADVISOR,
      isActive: location === ROUTES.ENERGY_ADVISOR,
      show: true
    },
    {
      id: 6,
      asset: Images.sidebarEnergySimulator,
      optionName: message('Sidebar:simulatorButton').toUpperCase(),
      externalLink: appConfig.simulatorLink,
      show: appConfig.simulatorButton
    }
  ].filter(menu => !(menu.options && menu.options.length === 0) && menu.show);

  const publicSideBarOptions = [
    {
      id: 1,
      asset: Images.sidebarBills,
      optionName: message('Sidebar:payments').toUpperCase(),
      linkTo: ROUTES.PUBLIC_PAYMENTS_BY_NIS,
      isActive: location === ROUTES.PUBLIC_PAYMENTS_BY_NIS || location === ROUTES.PUBLIC_PAYMENT_DOCUMENTS,
      show: appConfig.payments.publicPaymentByNis
    },
    {
      id: 2,
      asset: Images.sidebarBills,
      optionName: appConfig.payments.publicPayment
        ? message('Sidebar:documentsAndPayments').toUpperCase()
        : message('Sidebar:paymentDocuments').toUpperCase(),
      linkTo: ROUTES.PUBLIC_PAYMENTS,
      isActive:
        location === ROUTES.PUBLIC_PAYMENTS ||
        (!appConfig.payments.publicPaymentByNis && location === ROUTES.PUBLIC_PAYMENT_DOCUMENTS),
      show: appConfig.publicDashboard.publicPayment
    },
    {
      id: 3,
      asset: Images.sidebarTechnicalService,
      optionName: message('Sidebar:newClaim').toUpperCase(),
      key: !appConfig.claims.showClaimFormsOnSidebar && NEW_CLAIM,
      show: appConfig.publicDashboard.claims,
      options: appConfig.claims.showClaimFormsOnSidebar && [...claimSuboptions]
    },
    {
      id: 4,
      asset: Images.sidebarCustomerSupport,
      optionName: message('Sidebar:newProcedure').toUpperCase(),
      key: NEW_PROCEDURE,
      show: appConfig.publicDashboard.procedures
    },
    {
      id: 5,
      asset: Images.sidebarComplaints,
      optionName: message('Sidebar:complaints').toUpperCase(),
      linkTo: ROUTES.PUBLIC_COMPLAINTS,
      isActive: location === ROUTES.PUBLIC_COMPLAINTS,
      show: appConfig.publicDashboard.complaints
    },
    {
      id: 6,
      asset: Images.sidebarSocialRate,
      optionName: message('Sidebar:socialRate').toUpperCase(),
      linkTo: ROUTES.PUBLIC_SOCIAL_RATE,
      isActive: location === ROUTES.PUBLIC_SOCIAL_RATE,
      show: appConfig.publicDashboard.socialRate
    },
    {
      id: 7,
      asset: Images.sidebarLastBillInfo,
      optionName: message('Sidebar:lastBillQuery').toUpperCase(),
      linkTo: ROUTES.PUBLIC_LAST_BILL_QUERY,
      isActive: location === ROUTES.PUBLIC_LAST_BILL_QUERY,
      show: appConfig.publicDashboard.lastBillQuery
    },
    {
      id: 8,
      asset: Images.sidebarBalanceQuery,
      optionName: message('Sidebar:balanceQuery').toUpperCase(),
      linkTo: ROUTES.PUBLIC_BALANCE_QUERY,
      isActive: location === ROUTES.PUBLIC_BALANCE_QUERY,
      show: appConfig.publicDashboard.balanceQuery
    },
    {
      id: 9,
      asset: Images.sidebarSocialRate,
      optionName: message('Sidebar:changeDueDate').toUpperCase(),
      linkTo: ROUTES.PUBLIC_CHANGE_DUE_DATE,
      isActive: location === ROUTES.PUBLIC_CHANGE_DUE_DATE,
      show: appConfig.publicDashboard.changeDueDate
    },
    {
      id: 10,
      asset: Images.sidebarCustomerSupport,
      optionName: message('Sidebar:customerSupport').toUpperCase(),
      isActive:
        location === ROUTES.PUBLIC_PENDING_WARRANTY_DEPOSIT_QUERY ||
        location === ROUTES.PUBLIC_PENDING_FINE_AMOUNT_QUERY,
      show:
        appConfig.publicDashboard.pendingWarrantyDepositQuery ||
        appConfig.publicDashboard.pendingFineAmountQuery,
      options: [
        {
          optionName: message('Sidebar:consultDepositWarranty'),
          linkTo: ROUTES.PUBLIC_PENDING_WARRANTY_DEPOSIT_QUERY,
          isActive: location === ROUTES.PUBLIC_PENDING_WARRANTY_DEPOSIT_QUERY,
          show: appConfig.publicDashboard.pendingWarrantyDepositQuery
        },
        {
          optionName: message('Sidebar:consultFineQuality'),
          linkTo: ROUTES.PUBLIC_PENDING_FINE_AMOUNT_QUERY,
          isActive: location === ROUTES.PUBLIC_PENDING_FINE_AMOUNT_QUERY,
          show: appConfig.publicDashboard.pendingFineAmountQuery
        }
      ].filter(submenu => submenu.show)
    },
    {
      id: 11,
      asset: Images.sidebarEnergySimulator,
      optionName: message('Sidebar:simulatorButton').toUpperCase(),
      externalLink: appConfig.simulatorLink,
      show: appConfig.publicDashboard.simulator
    }
  ].filter(element => element.show);

  return !isPublic ? checkSubOptions(privateSideBarOptions) : checkSubOptions(publicSideBarOptions);
}

export default getSidebarOptions;
